import { useState } from 'react'

import { IconCheck, IconChevronDown, IconSearch } from '@tabler/icons-react'
import { Command as CommandPrimitive } from 'cmdk'

import { useFieldDescribedBy } from '@/components/sui/field'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/sui/popover'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { cn } from '@/lib/utils'

import locales from './locales'

interface Country {
  code: string | null
  displayName: string | null
}

interface CountryComboboxProps {
  value: string
  onValueChange: (value: string) => void
  countries: Array<Country>
  placeholder?: string
  disabled?: boolean
  'aria-invalid'?: boolean
  id?: string
}

export function CountryCombobox({
  value,
  onValueChange,
  countries,
  placeholder,
  disabled,
  'aria-invalid': ariaInvalid,
  id,
}: CountryComboboxProps) {
  const { t } = useComponentTranslation(locales)
  const [open, setOpen] = useState(false)
  const describedBy = useFieldDescribedBy()

  const selectedCountry = countries.find((c) => c.code === value)

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger
          render={<button type="button" />}
          id={id}
          disabled={disabled}
          aria-invalid={ariaInvalid}
          aria-describedby={describedBy}
          className={cn(
            'border-input data-[placeholder]:text-muted-foreground dark:bg-input/30 dark:hover:bg-input/50 focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 flex h-9 w-full cursor-pointer items-center justify-between gap-1.5 rounded-md border bg-transparent py-2 pr-2 pl-2.5 text-base whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none focus-visible:ring-[3px] disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:ring-[3px] md:text-sm',
          )}
        >
          <span
            className={cn(
              'flex-1 truncate text-left',
              !selectedCountry && 'text-muted-foreground',
            )}
          >
            {selectedCountry?.displayName ?? placeholder}
          </span>
          <IconChevronDown className="text-muted-foreground pointer-events-none size-4 shrink-0" />
        </PopoverTrigger>
        <PopoverContent align="start" className="w-(--anchor-width) p-0">
          <CommandPrimitive className="flex flex-col">
            <div className="flex items-center gap-2 border-b px-3 py-2">
              <IconSearch className="text-muted-foreground size-4 shrink-0" />
              <CommandPrimitive.Input
                placeholder={t('searchPlaceholder')}
                className="placeholder:text-muted-foreground w-full bg-transparent text-sm outline-none"
              />
            </div>
            <CommandPrimitive.List className="max-h-56 overflow-y-auto p-1">
              <CommandPrimitive.Empty className="text-muted-foreground py-6 text-center text-sm">
                {t('empty')}
              </CommandPrimitive.Empty>
              {countries.map((country) => (
                <CommandPrimitive.Item
                  key={country.code}
                  value={country.displayName ?? ''}
                  onSelect={() => {
                    onValueChange(country.code ?? '')
                    setOpen(false)
                  }}
                  className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm outline-hidden select-none"
                >
                  <span className="flex-1 truncate">{country.displayName}</span>
                  {country.code === value && (
                    <IconCheck className="text-link size-4 shrink-0" />
                  )}
                </CommandPrimitive.Item>
              ))}
            </CommandPrimitive.List>
          </CommandPrimitive>
        </PopoverContent>
      </Popover>
      {/* Base UI Select renders a trailing hidden input for form integration. The
          FieldDescription tuck (nth-last-2) keys off the field's child count, so mirror
          that trailing node here to keep a CountryCombobox aligned beside a Select. */}
      <input type="hidden" value={value} readOnly aria-hidden tabIndex={-1} />
    </>
  )
}
